import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { type MouseEvent, useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/button';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  deleteWebinar,
  fetchEvergreenConfiguration,
  publishWebinar,
} from '@/features/webinar/webinarThunks';
import { webinarService } from '@/services/webinar.service';
import 'react-circular-progressbar/dist/styles.css';
import CircularProgressWithCustomLabelDemo from '../ui/customized/progress/progress-09';
import { Check } from 'lucide-react';
import Loader from '../ui/Loader';
import { toast } from 'react-toastify';
import { mapHttpError } from '@/lib/mapApiError';
import { isImportedMp4Evergreen } from '@/lib/evergreenMp4File';
import type { AppError } from '@/features/auth/types';
import { PROGRAM_TAB_STATE } from '@/pages/webinars/WebinarsPage';

const items = [
  { to: '/create-webinar/configuration', key: 'configuration' },
  { to: '/create-webinar/signin', key: 'signin' },
  { to: '/create-webinar/waiting-room', key: 'waiting-room' },

  // evergreen
  { to: '/create-webinar/evergreen', key: 'evergreen' },
  { to: '/create-webinar/chat-evergreen', key: 'chat-evergreen' },
  // live
  { to: '/create-webinar/live', key: 'live' },
  { to: '/create-webinar/interactions', key: 'interactions' },

  { to: '/create-webinar/post-event', key: 'post-event' },
  { to: '/create-webinar/replay', key: 'replay' },
  { to: '/create-webinar/integration', key: 'integration' },
] as const;

export default function SideBarCreateWebinar({
  mobile,
  onNavigate,
}: Readonly<{
  mobile?: boolean;
  onNavigate?: () => void;
}>) {
  const { t } = useTranslation('common');
  const loaderGradientId = useId();
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = webinarIdParam;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const {
    currentWebinar,
    currentSessionConfiguration,
    presenters,
    moderators,
    currentWebinarSubscriptionPage,
    currentWaitingRoomPage,
    webinarSlides,
    quizTemplates,
    webinarResourcesTotalElements,
    surveyTemplates,
    currentReplayConfiguration,
    offerTemplates,
    currentThankYouPage,
    participationStatuses,
    createWebinarInitialLoading,
    currentQuitPage,
    currentEvergreenConfiguration,
  } = useAppSelector((state) => state.webinar);
  const navLinksDisabled = !currentWebinar;

  const isImportedMp4 = isImportedMp4Evergreen(currentSessionConfiguration);
  useEffect(() => {
    if (!isImportedMp4 || workspaceId == null || !currentWebinar?.id) return;
    void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: currentWebinar.id }));
  }, [dispatch, isImportedMp4, workspaceId, currentWebinar?.id]);

  const hasEvergreenVideo =
    !!currentSessionConfiguration?.evergreenSession ||
    (currentEvergreenConfiguration?.webinarId === currentWebinar?.id &&
      !!currentEvergreenConfiguration?.videoUrl);

  const sessionType = currentSessionConfiguration?.type ?? 'LIVE';
  const visibleItems = items.filter((item) => {
    if (item.key === 'live') return sessionType === 'LIVE';
    if (item.key === 'interactions') return sessionType === 'LIVE';
    if (item.key === 'evergreen' || item.key === 'chat-evergreen')
      return sessionType === 'EVERGREEN';
    return true;
  });

  const calculateProgress = () => {
    if (!currentWebinar?.id) return 0;

    let completedSteps = 0;
    const totalSteps = 14;

    // general 4 steps
    if (currentWebinar) {
      completedSteps += 1;
    }
    if (currentSessionConfiguration) {
      completedSteps += 1;
    }
    const webinarPresenters = presenters.filter((s) => s.webinarId === currentWebinar.id);
    if (webinarPresenters.length > 0) {
      completedSteps += 1;
    }
    const webinarModerators = moderators.filter((m) => m.webinarId === currentWebinar.id);
    if (webinarModerators.length > 0) {
      completedSteps += 1;
    }

    if (currentWebinarSubscriptionPage) {
      completedSteps += 1;
    }

    if (currentWaitingRoomPage) {
      completedSteps += 2;
    }

    if (webinarSlides.length > 0) {
      completedSteps += 1;
    }

    if (quizTemplates.length > 0) {
      completedSteps += 1;
    }

    if (webinarResourcesTotalElements > 0) {
      completedSteps += 1;
    }

    if (surveyTemplates.length > 0) {
      completedSteps += 1;
    }

    if (offerTemplates.length > 0) {
      completedSteps += 1;
    }

    if (currentReplayConfiguration) {
      completedSteps += 1;
    }

    if (currentThankYouPage) {
      completedSteps += 1;
    }

    return Math.min(Math.round((completedSteps / totalSteps) * 100), 100);
  };

  const progressPercentage = calculateProgress();

  const isConfigurationComplete = () => {
    if (!currentWebinar?.id) return false;
    if (!currentSessionConfiguration) return false;
    const webinarPresenters = presenters.filter((s) => s.webinarId === currentWebinar.id);
    if (webinarPresenters.length === 0) return false;
    const webinarModerators = moderators.filter((m) => m.webinarId === currentWebinar.id);
    if (webinarModerators.length === 0) return false;
    if (currentSessionConfiguration.type === 'EVERGREEN') {
      if (!hasEvergreenVideo) return false;
    } else if (
      !currentSessionConfiguration.nextDates?.length &&
      !currentSessionConfiguration.sessions?.length
    ) {
      return false;
    }

    return true;
  };

  const configurationComplete = isConfigurationComplete();

  const isWaitingRoomComplete = () => {
    if (!currentWaitingRoomPage) return false;

    return true;
  };
  const waitingRoomComplete = isWaitingRoomComplete();

  const isLiveComplete = () => {
    if (webinarSlides.length > 0) return true;
    if (quizTemplates.length > 0) return true;
    if (webinarResourcesTotalElements > 0) return true;
    if (surveyTemplates.length > 0) return true;
    if (offerTemplates.length > 0) return true;

    return false;
  };
  const liveComplete = isLiveComplete();

  // Les notifications ayant migré dans les offres, la page de sortie est le seul bloc de la
  // section evergreen qui reste un signal fiable : le back renvoie une page par défaut sans
  // aucune raison tant que rien n'a été enregistré.
  const evergreenComplete =
    currentWebinar?.id != null &&
    currentQuitPage?.webinarId === currentWebinar.id &&
    (currentQuitPage?.reasons?.length ?? 0) > 0;

  const isItemComplete = (key: (typeof items)[number]['key']): boolean => {
    switch (key) {
      case 'configuration':
        return configurationComplete;
      case 'signin':
        return Boolean(currentWebinarSubscriptionPage);
      case 'waiting-room':
        return waitingRoomComplete;
      case 'live':
        return liveComplete;
      case 'interactions':
      case 'chat-evergreen':
        return Boolean(participationStatuses?.length);
      case 'evergreen':
        return evergreenComplete;
      case 'replay':
        return Boolean(currentReplayConfiguration);
      case 'post-event':
        return Boolean(currentThankYouPage);
      default:
        return false;
    }
  };
  const MANDATORY_ITEM_KEYS = ['configuration', 'signin', 'waiting-room'] as const;
  const firstMissingMandatoryItem = visibleItems.find(
    (item) =>
      (MANDATORY_ITEM_KEYS as readonly string[]).includes(item.key) && !isItemComplete(item.key),
  );

  const getLinkUrl = (to: string) => {
    if (webinarId) {
      return `${to}/${webinarId}`;
    }
    return to;
  };

  const handleNavLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (navLinksDisabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onNavigate?.();
    },
    [navLinksDisabled, onNavigate],
  );

  const canPublish = useCallback(() => {
    if (!currentWebinar?.id) {
      return false;
    }

    if (!currentWaitingRoomPage) {
      return false;
    }

    if (!currentWebinarSubscriptionPage) {
      return false;
    }

    if (!currentSessionConfiguration) {
      return false;
    }

    if (currentSessionConfiguration.type === 'EVERGREEN') {
      if (!hasEvergreenVideo) {
        return false;
      }
    } else if (
      !currentSessionConfiguration.nextDates?.length &&
      !currentSessionConfiguration.sessions?.length
    ) {
      return false;
    }

    return true;
  }, [
    currentWebinar,
    presenters,
    moderators,
    currentWaitingRoomPage,
    currentWebinarSubscriptionPage,
    currentSessionConfiguration,
    hasEvergreenVideo,
  ]);

  const publishDisabled = !canPublish();
  const [isPublishing, setIsPublishing] = useState(false);

  const showPublishError = useCallback(
    (appError: AppError | undefined) => {
      if (appError?.details && Array.isArray(appError.details) && appError.details.length > 0) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            toast.error(t(mapHttpError(detail.message)));
          }
        });
        return;
      }

      toast.error(t(mapHttpError(appError?.message ?? 'errors.generic')));
    },
    [t],
  );

  const handlePublish = useCallback(async () => {
    if (!webinarId || !workspaceId || publishDisabled || isPublishing) {
      return;
    }

    setIsPublishing(true);
    try {
      const parsedWebinarId = parseInt(webinarId, 10);
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const templatesPage = await webinarService.listQuizTemplates(
          workspaceId,
          parsedWebinarId,
          page,
          50,
        );

        for (const template of templatesPage.content) {
          await webinarService.updateQuizTemplate(workspaceId, parsedWebinarId, template.id, {
            name: template.name,
            subTitle: template.subTitle,
            enabledDuringLive: template.enabledDuringLive,
            shuffleQuestions: template.shuffleQuestions,
            shuffleOptions: template.shuffleOptions,
            passingScore: template.passingScore,
            visibleFrom: template.visibleFrom,
            visibleUntil: template.visibleUntil,
            published: true,
            borderColor: template.borderColor,
            accentColor: template.accentColor,
            backgroundColor: template.backgroundColor,
            titleColor: template.titleColor,
            waitingRoomQuiz: template.waitingRoomQuiz,
            displayWaitingRoomQuiz: template.displayWaitingRoomQuiz,
          });
        }

        hasMore = page < templatesPage.totalPages - 1;
        page++;
      }

      // Publier le webinar
      const result = await dispatch(
        publishWebinar({
          workspaceId,
          id: parsedWebinarId,
        }),
      );

      if (publishWebinar.fulfilled.match(result)) {
        navigate('/webinars', { state: PROGRAM_TAB_STATE });
      } else if (publishWebinar.rejected.match(result)) {
        console.error('Error publishing webinar:', result.payload);
        showPublishError(result.payload as AppError | undefined);
      }
    } catch (error) {
      console.error('Error publishing webinar:', error);
      showPublishError(error as AppError | undefined);
    } finally {
      setIsPublishing(false);
    }
  }, [webinarId, workspaceId, dispatch, publishDisabled, isPublishing, navigate, showPublishError]);

  const handleAbandon = useCallback(async () => {
    if (!webinarId || !workspaceId) {
      navigate('/webinars', { state: PROGRAM_TAB_STATE });
      return;
    }

    try {
      const result = await dispatch(
        deleteWebinar({
          workspaceId,
          id: parseInt(webinarId, 10),
        }),
      );

      if (deleteWebinar.fulfilled.match(result)) {
        navigate('/webinars', { state: PROGRAM_TAB_STATE });
      } else if (deleteWebinar.rejected.match(result)) {
        // Un webinaire source d'evergreen n'est pas supprimable : quitter la page masquerait le refus.
        const appError = result.payload as AppError | undefined;
        toast.error(t(mapHttpError(appError?.message ?? 'errors.generic')));
      }
    } catch (error) {
      console.error('Error deleting webinar:', error);
      toast.error(t('errors.generic'));
    }
  }, [webinarId, workspaceId, dispatch, navigate, t]);

  return (
    <div
      className={`h-screen w-[216px] flex flex-col justify-between px-4 py-6 top-0 sticky ${mobile && 'rounded-md'}`}
      style={{
        background:
          'linear-gradient(224deg, #0C0E21 -25.68%, #303763 29.61%, #171A35 78.19%, #0C0E21 104.6%)',
      }}
    >
      <div className="flex flex-col gap-4">
        {visibleItems.map((item) => {
          const shouldShowComplete = isItemComplete(item.key);

          return (
            <NavLink
              key={item.key}
              to={getLinkUrl(item.to)}
              onClick={handleNavLinkClick}
              tabIndex={navLinksDisabled ? -1 : undefined}
              aria-disabled={navLinksDisabled}
              className={({ isActive }) =>
                [
                  'relative flex items-center gap-3 px-4 py-2  text-sm font-medium rounded-full hover:outline hover:outline-white/12',
                  isActive
                    ? 'text-[#2356CC] bg-white hover:text-[#2356CC] hover:bg-white'
                    : shouldShowComplete
                      ? 'text-white bg-[#17805C] hover:bg-[#17805C]'
                      : 'text-white bg-[#0C0E21] hover:bg-white/10',
                  navLinksDisabled ? 'opacity-40 cursor-not-allowed' : '',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span className="flex-1">{t(`sidebar.createWebinar.${item.key}`)}</span>
                  <div className="ml-auto flex items-center gap-2">
                    {shouldShowComplete && <Check className="text-success size-5 shrink-0" />}
                  </div>
                  {isActive && !mobile && (
                    <div className="absolute top-0 -right-4 h-9 w-15 bg-white">
                      <img
                        src="/assets/1.svg"
                        alt={item.key}
                        className="absolute right-0 top-5 h-auto w-13 -translate-y-1/2"
                      />
                    </div>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>

      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center gap-2 p-4 bg-white/20 rounded-[20px] min-h-33">
          {webinarId && createWebinarInitialLoading ? (
            <>
              <p className="text-xs text-white font-semibold text-center">
                {t('sidebar.createWebinar.progressLoading')}
              </p>
              <div className="w-24 h-24">
                <svg
                  width={96}
                  height={96}
                  viewBox="-12 -12 120 120"
                  className="animate-spin"
                  style={{ animationDuration: '1.2s' }}
                >
                  <defs>
                    <linearGradient
                      id={loaderGradientId}
                      gradientUnits="objectBoundingBox"
                      gradientTransform="rotate(226 .5 .5)"
                    >
                      <stop offset="-0.36%" stopColor="#2C6CFF" />
                      <stop offset="104.08%" stopColor="#27D99C" />
                    </linearGradient>
                  </defs>
                  <circle
                    r={38}
                    cx={48}
                    cy={48}
                    fill="transparent"
                    strokeWidth={12}
                    stroke={`url(#${loaderGradientId})`}
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 38}`}
                    strokeDashoffset={2 * Math.PI * 38 * 0.75}
                  />
                </svg>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-white font-semibold text-center">
                {publishDisabled
                  ? firstMissingMandatoryItem
                    ? t('sidebar.createWebinar.progressHintMissing', {
                        step: t(`sidebar.createWebinar.${firstMissingMandatoryItem.key}`),
                      })
                    : t('sidebar.createWebinar.progressHintMissingGeneric')
                  : t('sidebar.createWebinar.progressHintComplete')}
              </p>
              <div className="w-24">
                <CircularProgressWithCustomLabelDemo value={progressPercentage} />
              </div>
            </>
          )}
        </div>
        <Button
          disabled={!webinarId}
          onClick={() => navigate('/webinars', { state: PROGRAM_TAB_STATE })}
          className="w-full disabled:bg-button-primary-disable-inverse disabled:text-disable"
        >
          {t('sidebar.createWebinar.draft')}
        </Button>
        <Button
          disabled={publishDisabled || isPublishing}
          onClick={handlePublish}
          className="w-full disabled:bg-button-primary-disable-inverse disabled:text-disable"
        >
          {isPublishing && <Loader />}
          {t('sidebar.createWebinar.publish')}
        </Button>
        <Button
          variant="outline"
          className="w-full text-sm text-button-secondary font-medium bg-transparent border-none"
          onClick={handleAbandon}
        >
          {t('sidebar.createWebinar.delete')}
        </Button>
      </div>
    </div>
  );
}
